<template>
    <div class="resort-page">
        <main-page-form-wrapper :img="resort.background" :city-id="resort.cityId" />
        <v-container fluid>
            <h1 class="h-light mt-12 mb-6">
                {{ city.name }}
            </h1>
            <template v-if="$breakpoint.mdAndUp">
                <v-tabs v-model="descriptionTabIndex" grow center-active class="description-tabs mb-6">
                    <v-tab v-for="(descriptionTab, index) in descriptionTabs" :key="index">
                        {{ descriptionTab.title }}
                    </v-tab>
                </v-tabs>
                <v-window v-model="descriptionTabIndex" class="overflow--visible">
                    <v-window-item v-for="(descriptionTab, index) in descriptionTabs" :key="index">
                        <div v-if="descriptionTab.key !== 'gallery'" v-html="markdown(descriptionTab.content)" />
                        <cms-gallery v-else :items="[...resort.video, ...descriptionTab.content]" />
                    </v-window-item>
                </v-window>
            </template>
            <template v-else>
                <v-expansion-panels variant="accordion">
                    <v-expansion-panel v-for="(descriptionTab, index) in descriptionTabs" :key="index">
                        <v-expansion-panel-title>
                            {{ descriptionTab.title }}
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <div v-if="descriptionTab.key !== 'gallery'" v-html="markdown(descriptionTab.content)" />
                            <cms-gallery v-else :items="[...resort.video, ...descriptionTab.content]" />
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </template>
            <popular-destinations
                v-if="resort.popular_destinations && resort.popular_destinations.length"
                :destinations="resort.popular_destinations"
                class="mt-14"
            />
            <blog v-if="resort.blogs && resort.blogs.length" :items="resort.blogs" class="mt-14" />
        </v-container>
    </div>
</template>

<script lang="ts">
    import {Vue, Component} from 'vue-facing-decorator'
    import PopularDestinations from '~src/components/parts/popularDestinations.src'
    import Blog from '~src/components/parts/blog.src'
    import {CHANGE_LOCALE_EVENT, EventBus} from '@/utils/event-bus'
    import CmsGallery from '@/components/snippets/CmsGallery.vue'
    import {clone} from '@/utils/helpers'
    import MainPageFormWrapper from '~src/components/parts/mainPageFormWrapper.src'
    import {fullUrl} from '~src/utils/host-adapter'
    import MarkdownIt from 'markdown-it/dist/markdown-it.js'

    const md = new MarkdownIt('default', {html: true})

    @Component({
        components: {
            CmsGallery,
            MainPageFormWrapper,
            Blog,
            PopularDestinations,
        },
        async setup() {
            const app = useNuxtApp()
            const $route = useRoute()
            const link = $route.params.link
            const $api = app.$api
            const {data} = await useAsyncData(`resort-${link}`, async () => {
                try {
                    const resort = await $api.resort.get(link),
                        {
                            cities: [city],
                        } = await $api.locations.get({id: resort.cityId, limitCities: 1})
                    return {resort, city}
                } catch (e) {
                    throw createError({statusCode: e.status, message: e.msg})
                }
            })
            useHead(ResortPage.__vccOpts.methods.head({$route, ...data.value}))
            return {resort: data.value.resort, city: data.value.city}
        },
    })
    export class ResortPage extends Vue {
        resort = {}
        productTabs = 0
        descriptionTabIndex = 0
        city = {}

        mounted() {
            EventBus.$on(CHANGE_LOCALE_EVENT, this.load)
        }

        beforeUnmount() {
            EventBus.$off(CHANGE_LOCALE_EVENT, this.load)
        }

        head({$route, resort}) {
            if (!cmsStore.layoutSettings.head) return null
            const head = clone(cmsStore.layoutSettings.head)
            head.link = [
                {
                    rel: 'canonical',
                    href: fullUrl($route.path),
                },
            ]
            if (resort.seo?.title) {
                head.titleTemplate = resort.seo.title
            }
            resort.seo?.meta.forEach(meta => {
                const metaIndex = head.meta.findIndex(({hid}) => hid === meta.hid)
                const {hid, name, content} = meta
                if (metaIndex !== -1) {
                    head.meta[metaIndex] = {hid, name, content}
                } else {
                    head.meta.push({hid, name, content})
                }
            })
            return head
        }

        async load() {
            try {
                this.resort = await this.$api.resort.get(this.$route.params.link)
                const {
                    cities: [city],
                } = await this.$api.locations.get({id: this.resort.cityId, limitCities: 1})
                this.city = city
            } catch (e) {
                this.resort = {}
                this.city = {}
            }
        }

        markdown(text) {
            return md.render(text)
        }

        get descriptionTabs() {
            return ['description', 'specialization', 'spa', 'leisure', 'gallery']
                .filter(
                    key =>
                        ((key !== 'gallery' && this.resort[key]) ||
                            (key === 'gallery' && (this.resort.gallery?.length || this.resort.video?.length))) &&
                        this.resort[`${key}Active`]
                )
                .map(key => ({
                    key,
                    content: this.resort[key],
                    title: this.resort[`${key}Title`] || this.$t('resortTab')[key],
                }))
        }
    }

    export default ResortPage
</script>

<style scoped lang="scss">
    .v-expansion-panels {
        z-index: 1500;
    }
    .resort-page {
        :deep(img) {
            max-width: 100%;
        }
    }
</style>
